import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, ArrowLeftRight, Store, Gavel, Users, Settings, Search, Maximize, AlertCircle, LayoutGrid, List } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { gameRedisService } from '../services/gameRedis';
import { configService } from '../services/usersApi';
import { prefetchService } from '../services/prefetchService';
import { EconomyLogData, ShopData, ServerConfig } from '../types/player';
import { ItemIcon } from '../components/ItemIcon';
import { usernameCacheService } from '../services/usernameCache';
import { PlayerProfile } from '../components/PlayerProfile';
import { ClusterSelector } from '../components/ClusterSelector';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { cn } from '../utils/cn';
import { ExpandedLogModal } from '../components/ExpandedLogModal';
import { TransactionLogEntry } from '../components/TransactionLogEntry';
import { LogPanelSkeleton } from '../components/LogPanelSkeleton';
import { VirtualizedLogList } from '../components/VirtualizedLogList';
import { RedisKeyInfo } from '../components/RedisKeyInfo';
import { useIsMobile } from '../hooks/useIsMobile';
import { LegacyMinecraftText, stripLegacyMinecraftFormatting } from '../components/LegacyMinecraftText';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { DataFreshness } from '../components/DataFreshness';
import { useSwrLoader } from '../hooks/useSwrLoader';
import { read } from '../services/swrCache';


type ShopCategory = 'ah' | 'as' | 'cs';

function adminShopPriceDefined(raw: unknown): boolean {
  return raw !== undefined && raw !== null && raw !== '';
}

function adminShopHasAnyPricedSide(entry: { buyPrice?: unknown; sellPrice?: unknown }): boolean {
  const buy = adminShopPriceDefined(entry.buyPrice) ? Number(entry.buyPrice) : NaN;
  const sell = adminShopPriceDefined(entry.sellPrice) ? Number(entry.sellPrice) : NaN;
  return (Number.isFinite(buy) && buy !== -1) || (Number.isFinite(sell) && sell !== -1);
}

function AdminShopPriceValue({ kind, raw }: { kind: 'buy' | 'sell'; raw: unknown }) {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return <span className="text-glass-400">{String(raw)}</span>;
  }
  if (n === -1) {
    return (
      <span className="text-glass-500 font-medium">
        {kind === 'buy' ? 'not purchasable' : 'not for sale'}
      </span>
    );
  }
  return (
    <span className={cn('font-semibold', kind === 'buy' ? 'text-green-400' : 'text-blue-400')}>
      ₡{n}
    </span>
  );
}

interface ExpandedLogState {
  logs: any[];
  title: string;
  key?: string | null;
}

const CARD_WIDTH = 200;
const CARD_HEIGHT = 220;
const GAP = 16;

const ListingCardContent = memo(({ item, clusterId }: { item: any, clusterId: string }) => (
  <>
    <ItemIcon displayMode="card" clusterId={clusterId} itemId={item.itemStack.id} />
    <div className="text-xs text-glass-300 space-y-1 border-t border-glass-200/10 pt-2">
      <div className="flex justify-between items-center"><span>Seller:</span> <PlayerProfile uuid={item.seller} name={item.sellerName} /></div>
      <div className="flex justify-between"><span>Price:</span> <span className="text-green-400 font-semibold">₡{item.price}</span></div>
      <div className="flex justify-between"><span>Stock:</span> <span className="text-white">{item.count}</span></div>
      <div className="flex justify-between"><span>Expires:</span> <span className="text-white">{new Date(item.expires).toLocaleDateString()}</span></div>
    </div>
  </>
));

const VirtualizedShopGrid = memo(({ listings, clusterId }: { listings: any[], clusterId: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        setSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const { width, height } = size;
  
  const columnCount = width > 0 ? Math.max(1, Math.floor(width / (CARD_WIDTH + GAP))) : 1;
  const rowCount = Math.ceil(listings.length / columnCount);

  const rowVirtualizer = useVirtualizer({ 
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CARD_HEIGHT + GAP,
    overscan: 2,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: columnCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CARD_WIDTH + GAP,
    overscan: 2,
  });

  return (
    <div ref={containerRef} className="h-[60vh] w-full">
      {height > 0 && width > 0 && (
        <div ref={parentRef} style={{ height: `${height}px`, width: `${width}px`, overflow: 'auto' }}>
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: `${columnVirtualizer.getTotalSize()}px`,
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) =>
              columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                const index = virtualRow.index * columnCount + virtualColumn.index;
                if (index >= listings.length) return null;
                const item = listings[index];

                return (
                  <div
                    key={`${virtualRow.index}-${virtualColumn.index}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${virtualColumn.size}px`,
                      height: `${virtualRow.size}px`,
                      transform: `translateX(${virtualColumn.start}px) translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <div style={{ padding: `${GAP / 2}px` }} className="h-full w-full">
                      <div className="subcard p-3 space-y-3 flex flex-col justify-between h-full">
                        <ListingCardContent item={item} clusterId={clusterId} />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
});
const ListingsComponent = memo(({ listings, loading, searchQuery, clusterId, category }: { listings: any[] | undefined, loading: boolean, searchQuery: string, clusterId: string, category: string }) => {
  const filteredListings = useMemo(() => {
    const activeListings = listings?.filter(item => item.enabled) || [];
    if (!searchQuery) return activeListings;
    
    const lowerCaseQuery = searchQuery.toLowerCase();
    return activeListings.filter(item => {
      return (
        item.itemStack.id.toLowerCase().includes(lowerCaseQuery) ||
        (item.itemDisplayName && item.itemDisplayName.toLowerCase().includes(lowerCaseQuery)) ||
        item.seller.toLowerCase().includes(lowerCaseQuery) ||
        item.sellerName.toLowerCase().includes(lowerCaseQuery) ||
        String(item.price).includes(lowerCaseQuery)
      );
    });
  }, [listings, searchQuery]);

  if (loading) return (
    <div className="space-y-4" aria-hidden="true">
      <SkeletonLoader className="h-4 w-48" />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <SkeletonLoader key={i} className="h-44" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionHeader title={`${category} Listings`} icon={ShoppingCart} iconClassName="text-green-400" count={filteredListings.length} className="mb-0" />
      <div className="pr-2">
        {filteredListings.length === 0 ? <EmptyState bare compact icon={ShoppingCart} title="No active listings" description="Nothing matches your search on this cluster." /> : (
          <VirtualizedShopGrid listings={filteredListings} clusterId={clusterId} />
        )}
      </div>
    </div>
  );
});



const AdminShopItemImage = memo(({ clusterId, itemId, onDisplayNameLoad }: { clusterId: string, itemId: string, onDisplayNameLoad?: (displayName: string | null) => void }) => {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!itemId || !itemId.includes(':')) {
      setError(true);
      setIsLoading(false);
      return;
    }

    const [namespace, id] = itemId.split(':');
    const primaryUrl = `https://atlas.playcdu.co/img/${clusterId}/${namespace}/${id}/0`;
    const fallbackUrl = `https://atlas.playcdu.co/search/first/${namespace}/${id}`;

    const handleFetch = async (url: string, isFallback = false) => {
      try {
        const response = await fetch(url);
        if (response.ok) {
          setImgSrc(response.url);
          setError(false);
          
          // Extract display name from headers
          const displayNameHeader = response.headers.get('X-Display-Name');
          const displayName = displayNameHeader ? decodeURIComponent(displayNameHeader).replace(/\+/g, ' ') : null;
          onDisplayNameLoad?.(displayName);
        } else if (!isFallback) {
          handleFetch(fallbackUrl, true);
        } else {
          setError(true);
        }
      } catch (e) {
        if (!isFallback) {
          handleFetch(fallbackUrl, true);
        } else {
          setError(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    handleFetch(primaryUrl);
  }, [clusterId, itemId, onDisplayNameLoad]);

  if (error) {
    return <div className="w-8 h-8 bg-glass-200/10 rounded flex items-center justify-center text-xs text-glass-400">?</div>;
  }

  if (isLoading) {
    return <div className="w-8 h-8 skeleton"></div>;
  }

  return imgSrc ? (
    <img 
      src={imgSrc} 
      alt={itemId}
      className="w-8 h-8 object-contain"
      onError={() => setError(true)}
    />
  ) : null;
});

const AdminShopTooltip = memo(({ entry, slotIndex }: { entry: any, slotIndex: number }) => {
  const fallbackItemName = entry.stack.components?.['minecraft:item_name'] || entry.stack.id;
  const itemDisplayName = entry._displayName || fallbackItemName;
  
  return (
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
      <div className="glass-card p-3 min-w-[200px]">
        <div className="text-sm font-semibold text-white mb-2">
          <LegacyMinecraftText text={itemDisplayName} />
        </div>
        <div className="space-y-1 text-xs text-glass-300">
          {entry.type === 'PAGE' ? (
            <div className="flex justify-between">
              <span>Type:</span>
              <span className="text-blue-400 font-semibold">Page Navigation</span>
            </div>
          ) : (
            <>
              {adminShopPriceDefined(entry.buyPrice) && (
                <div className="flex justify-between">
                  <span>Buy Price:</span>
                  <AdminShopPriceValue kind="buy" raw={entry.buyPrice} />
                </div>
              )}
              {adminShopPriceDefined(entry.sellPrice) && (
                <div className="flex justify-between">
                  <span>Sell Price:</span>
                  <AdminShopPriceValue kind="sell" raw={entry.sellPrice} />
                </div>
              )}
              <div className="flex justify-between">
                <span>Quantity:</span>
                <span className="text-white">{entry.count || 'N/A'}</span>
              </div>
            </>
          )}
          <div className="flex justify-between">
            <span>Slot:</span>
            <span className="text-glass-400">{slotIndex}</span>
          </div>
          <div className="flex justify-between">
            <span>Item ID:</span>
            <span className="text-glass-400 text-xs truncate ml-2">{entry.stack.id}</span>
          </div>
        </div>
        {/* Tooltip arrow */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-glass-600"></div>
      </div>
    </div>
  );
});

const AdminShopListingCard = memo(({ entry, clusterId }: { entry: any, clusterId: string }) => {
  const fallbackItemName = entry.stack.components?.['minecraft:item_name'] || entry.stack.id;
  
  return (
    <div className="relative group">
      <div className="w-16 h-16 flex items-center justify-center bg-glass-200/5 border border-glass-200/15 rounded-md hover:bg-glass-200/10 transition-colors cursor-pointer">
        <ItemIcon displayMode="inline" clusterId={clusterId} itemId={entry.stack.id} />
        {entry.stack.count > 1 && (
          <span className="absolute bottom-0 right-0 text-xs text-white bg-black/50 px-1 rounded">
            {entry.stack.count}
          </span>
        )}
        {adminShopHasAnyPricedSide(entry) && (
          <div className="absolute top-0 left-0 w-2 h-2 bg-green-400 rounded-full"></div>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
        <div className="glass-card p-3 min-w-[200px]">
          <div className="text-sm font-semibold text-white mb-2 truncate">
            <LegacyMinecraftText text={fallbackItemName} />
          </div>
          <div className="space-y-1 text-xs text-glass-300">
            {adminShopPriceDefined(entry.buyPrice) && (
              <div className="flex justify-between">
                <span>Buy Price:</span>
                <AdminShopPriceValue kind="buy" raw={entry.buyPrice} />
              </div>
            )}
            {adminShopPriceDefined(entry.sellPrice) && (
              <div className="flex justify-between">
                <span>Sell Price:</span>
                <AdminShopPriceValue kind="sell" raw={entry.sellPrice} />
              </div>
            )}
            <div className="flex justify-between">
              <span>Stock:</span>
              <span className="text-white">{entry.count || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span>Slot:</span>
              <span className="text-glass-400">{entry.slot}</span>
            </div>
            <div className="flex justify-between">
              <span>Item ID:</span>
              <span className="text-glass-400 text-xs truncate ml-2">{entry.stack.id}</span>
            </div>
          </div>
          {/* Tooltip arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-glass-600"></div>
        </div>
      </div>
    </div>
  );
});

const AdminShopGrid = memo(({ allEntries, filteredListings, clusterId, searchQuery, onPageNavigation }: { allEntries: any[], filteredListings: any[], clusterId: string, searchQuery: string, onPageNavigation?: (pageId: string) => void }) => {
  // Create a 9x5 grid (45 slots total, 0-44)
  const GRID_WIDTH = 9;
  const GRID_HEIGHT = 5;
  const TOTAL_SLOTS = GRID_WIDTH * GRID_HEIGHT;

  // Create slot mapping for all entries (both LISTING and PAGE types)
  const slotMap = useMemo(() => {
    const map: { [key: number]: any } = {};
    allEntries.forEach(entry => {
      if (entry.slot >= 0 && entry.slot < TOTAL_SLOTS) {
        map[entry.slot] = entry;
      }
    });
    return map;
  }, [allEntries]);

  // Highlight filtered items
  const highlightedSlots = useMemo(() => {
    if (!searchQuery) return new Set();
    const slots = new Set<number>();
    filteredListings.forEach(entry => {
      if (entry.slot >= 0 && entry.slot < TOTAL_SLOTS) {
        slots.add(entry.slot);
      }
    });
    return slots;
  }, [filteredListings, searchQuery]);

  return (
    <div className="flex justify-center">
      <div className="inline-block subcard p-4">
        <div className="grid grid-cols-9 gap-1" style={{ width: 'fit-content' }}>
          {Array.from({ length: TOTAL_SLOTS }, (_, index) => {
            const entry = slotMap[index];
            const isHighlighted = highlightedSlots.has(index);
            const isFiltered = searchQuery && !isHighlighted;

            return (
              <div key={index} className="relative group">
                <div
                  className={cn(
                    "w-16 h-16 border border-glass-200/15 rounded-md bg-glass-200/5 flex items-center justify-center relative",
                    entry ? "hover:bg-glass-200/10 cursor-pointer" : "",
                    isFiltered ? "opacity-30" : "",
                    isHighlighted ? "ring-2 ring-accent-400" : "",
                    entry?.type === 'PAGE' ? "border-blue-400/50" : "" // Highlight page navigation items
                  )}
                  onClick={() => {
                    if (entry?.type === 'PAGE' && onPageNavigation) {
                      onPageNavigation(entry.pageId);
                    }
                  }}
                >
                  {entry ? (
                    <>
                      <AdminShopItemImage 
                        clusterId={clusterId} 
                        itemId={entry.stack.id} 
                        onDisplayNameLoad={(name) => {
                          // Store display name for tooltip use
                          if (name) {
                            entry._displayName = name;
                          }
                        }}
                      />
                      {entry.stack.count > 1 && (
                        <span className="absolute bottom-0 right-0 text-xs text-white bg-black/50 px-1 rounded">
                          {entry.stack.count}
                        </span>
                      )}
                      {adminShopHasAnyPricedSide(entry) && (
                        <div className="absolute top-0 left-0 w-2 h-2 bg-green-400 rounded-full"></div>
                      )}
                    </>
                  ) : (
                    <div className="text-glass-500 text-xs">{index}</div>
                  )}
                </div>
                
                {/* Tooltip for items */}
                {entry && (
                  <AdminShopTooltip entry={entry} slotIndex={index} />
                )}
              </div>
            );
          })}
        </div>
        
        {searchQuery && filteredListings.length < allEntries.length && (
          <div className="mt-2 text-center text-xs text-glass-400 tabular-nums">
            Showing {filteredListings.length} of {allEntries.length} items
          </div>
        )}
      </div>
    </div>
  );
});

const AdminShopList = memo(({ filteredListings, clusterId }: { filteredListings: any[], clusterId: string }) => (
  <div className="h-[60vh] w-full">
    <div className="h-full w-full overflow-auto">
      <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 xl:grid-cols-16 gap-2 p-4">
        {filteredListings.map((entry: any, index: number) => (
          <AdminShopListingCard key={`${entry.slot}-${index}`} entry={entry} clusterId={clusterId} />
        ))}
      </div>
    </div>
  </div>
));

const AdminShopComponent = memo(({ shopData, loading, searchQuery, clusterId }: { shopData: any, loading: boolean, searchQuery: string, clusterId: string }) => {
  const isMobile = useIsMobile();
  const [currentPageId, setCurrentPageId] = useState<string>('main');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(isMobile ? 'list' : 'grid');
  
  // Reset to main page when shopData changes (when switching clusters)
  useEffect(() => {
    setCurrentPageId('main');
  }, [shopData]);
  
  const parsedData = useMemo(() => {
    if (!shopData) return null;
    
    // Check if we have the expected ShopData structure with listings array
    if (shopData.listings && Array.isArray(shopData.listings)) {
      // For admin shop, the data structure is different - it should contain the JSON string
      if (shopData.listings.length > 0 && shopData.listings[0]["JSON.GET"]) {
        try {
          return JSON.parse(shopData.listings[0]["JSON.GET"]);
        } catch (error) {
          console.error('Failed to parse admin shop JSON:', error);
          return null;
        }
      }
      return null;
    }
    
    // If it's a string (like the URL), it means the key doesn't exist or there's an error
    if (typeof shopData === 'string') {
      console.warn('Admin shop data is a string (likely missing key):', shopData);
      return null;
    }
    
    // The admin shop data might be directly in shopData
    return shopData;
  }, [shopData]);

  const currentPage = useMemo(() => {
    if (!parsedData?.pages) return null;
    return parsedData.pages.find((page: any) => page.id === currentPageId) || parsedData.pages[0];
  }, [parsedData, currentPageId]);

  const { listings, allEntries } = useMemo(() => {
    if (!currentPage?.entries) return { listings: [], allEntries: [] };
    
    const listings = currentPage.entries.filter((entry: any) => entry.type === 'LISTING');
    const allEntries = currentPage.entries; // All entries including both LISTING and PAGE types
    
    return { listings, allEntries };
  }, [currentPage]);

  const filteredListings = useMemo(() => {
    if (!searchQuery) return listings;
    
    const lowerCaseQuery = searchQuery.toLowerCase();
    return listings.filter((entry: any) => {
      const itemName = entry.stack.components?.['minecraft:item_name'] || entry.stack.id || '';
      const itemSearch = stripLegacyMinecraftFormatting(itemName).toLowerCase();
      return (
        entry.stack.id.toLowerCase().includes(lowerCaseQuery) ||
        itemSearch.includes(lowerCaseQuery) ||
        String(entry.buyPrice || '').includes(lowerCaseQuery) ||
        String(entry.sellPrice || '').includes(lowerCaseQuery)
      );
    });
  }, [listings, searchQuery]);

  if (loading) return (
    <div className="space-y-4" aria-hidden="true">
      <SkeletonLoader className="h-4 w-48" />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <SkeletonLoader key={i} className="h-44" />)}
      </div>
    </div>
  );

  if (!parsedData) {
    return (
      <div className="space-y-4">
        <SectionHeader title="Admin Shop" description="Error parsing data" icon={Store} iconClassName="text-green-400" className="mb-0" />
        <EmptyState bare compact tone="danger" icon={AlertCircle} title="Failed to parse admin shop data structure." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap min-w-0">
          <Store className="w-4 h-4 text-green-400 shrink-0" />
          <span className="flex items-center gap-2 flex-wrap">
            Admin Shop
            <span className="text-glass-500 font-normal">/</span>
            <LegacyMinecraftText text={currentPage?.name || 'Unknown Page'} />
            <span className="badge badge-neutral">{filteredListings.length} items</span>
          </span>
        </h2>
        
        {parsedData?.pages && parsedData.pages.length > 1 && (
          <div className="segmented" role="group" aria-label="Shop pages">
            {parsedData.pages.map((page: any) => (
              <button
                key={page.id}
                type="button"
                onClick={() => setCurrentPageId(page.id)}
                data-active={currentPageId === page.id ? 'true' : 'false'}
                className="segmented-item segmented-item-sm"
              >
                <LegacyMinecraftText text={page.name} />
              </button>
            ))}
          </div>
        )}
      </div>

      {currentPage?.description && currentPage.description.length > 0 && (
        <div className="text-glass-400 text-xs space-y-0.5">
          {currentPage.description.map((line: string, i: number) => (
            <div key={i}>
              <LegacyMinecraftText text={line} />
            </div>
          ))}
        </div>
      )}

      <div className="pr-2">
        {listings.length === 0 ? (
          <EmptyState bare compact icon={Store} title="No items on this page" description="This admin shop page has no listings." />
        ) : (
          <div className="space-y-4">
            {/* Grid View Toggle */}
            <div className="flex justify-end">
              <div className="segmented" role="group" aria-label="View mode">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  data-active={viewMode === 'grid' ? 'true' : 'false'}
                  className="segmented-item segmented-item-sm"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  Grid View
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  data-active={viewMode === 'list' ? 'true' : 'false'}
                  className="segmented-item segmented-item-sm"
                >
                  <List className="w-3.5 h-3.5" />
                  List View
                </button>
              </div>
            </div>

            {viewMode === 'grid' ? (
              <AdminShopGrid 
                allEntries={allEntries} 
                filteredListings={filteredListings} 
                clusterId={clusterId} 
                searchQuery={searchQuery} 
                onPageNavigation={setCurrentPageId}
              />
            ) : (
              <AdminShopList filteredListings={filteredListings} clusterId={clusterId} />
            )}
          </div>
        )}
      </div>
    </div>
  );
});


// Categorized transaction logs
const AuctionHouseLog = memo(({ economyLog, loading, searchQuery, clusterId, onExpand, redisKey }: { economyLog: EconomyLogData | null, loading: boolean, searchQuery: string, clusterId: string, onExpand: (logs: any[], title: string, key: string | null) => void, redisKey: string | null }) => {
  const logs = useMemo(() => {
    let baseLogs = economyLog?.XRANGE.filter(entry => 
      ['BUY_AH', 'SELL_AH', 'LISTED_AH'].includes(entry.msg.action)
    ) || [];

    if (searchQuery) {
        const lowerCaseQuery = searchQuery.toLowerCase();
        return baseLogs.filter(entry => {
            const { user, item, itemDisplayName, money, action } = entry.msg;
            const username = usernameCacheService.getName(user)?.toLowerCase();
            return (
                user.toLowerCase().includes(lowerCaseQuery) ||
                (username && username.includes(lowerCaseQuery)) ||
                (item && item.toLowerCase().includes(lowerCaseQuery)) ||
                (itemDisplayName && itemDisplayName.toLowerCase().includes(lowerCaseQuery)) ||
                money.includes(lowerCaseQuery) ||
                action.toLowerCase().includes(lowerCaseQuery)
            );
        });
    }
    return baseLogs.slice().reverse();
  }, [economyLog, searchQuery]);

  if (loading) return <LogPanelSkeleton />;

  return (
    <div className="glass-card p-4 md:p-5 flex flex-col">
      <SectionHeader
        title="Auction House"
        icon={Gavel}
        iconClassName="text-blue-400"
        count={logs.length}
        actions={
          <>
            <RedisKeyInfo redisKey={redisKey} />
            <button type="button" onClick={() => onExpand(logs, 'Auction House Log', redisKey)} className="btn-icon btn-icon-sm" title="Expand Log View" aria-label="Expand Log View">
              <Maximize className="w-4 h-4" />
            </button>
          </>
        }
      />
      <div className="flex-grow">
        {logs.length === 0 ? <EmptyState bare compact icon={Gavel} title="No auction house transactions" /> : 
          <VirtualizedLogList
            items={logs}
            renderItem={(entry) => (
              <div key={entry.id} className="border-b border-glass-200/10 py-2">
                <div className="flex justify-between items-center gap-2 text-xs text-glass-400 mb-1 tabular-nums">
                  <span>{new Date(parseInt(entry.id.split('-')[0])).toLocaleString()}</span>
                  <span className="badge badge-info font-mono">{entry.msg.action}</span>
                </div>
                <TransactionLogEntry entry={entry.msg} clusterId={clusterId} />
              </div>
            )}
          />
        }
      </div>
    </div>
  );
});

const ShopLog = memo(({ economyLog, loading, searchQuery, clusterId, onExpand, redisKey }: { economyLog: EconomyLogData | null, loading: boolean, searchQuery: string, clusterId: string, onExpand: (logs: any[], title: string, key: string | null) => void, redisKey: string | null }) => {
  const logs = useMemo(() => {
    let baseLogs = economyLog?.XRANGE.filter(entry => 
      ['BUY_AS', 'SELL_AS', 'BUY_CS', 'SELL_CS'].includes(entry.msg.action)
    ) || [];

    if (searchQuery) {
        const lowerCaseQuery = searchQuery.toLowerCase();
        return baseLogs.filter(entry => {
            const { user, item, itemDisplayName, money, action } = entry.msg;
            const username = usernameCacheService.getName(user)?.toLowerCase();
            return (
                user.toLowerCase().includes(lowerCaseQuery) ||
                (username && username.includes(lowerCaseQuery)) ||
                (item && item.toLowerCase().includes(lowerCaseQuery)) ||
                (itemDisplayName && itemDisplayName.toLowerCase().includes(lowerCaseQuery)) ||
                money.includes(lowerCaseQuery) ||
                action.toLowerCase().includes(lowerCaseQuery)
            );
        });
    }

    return baseLogs.slice().reverse();
  }, [economyLog, searchQuery]);

  if (loading) return <LogPanelSkeleton />;

  return (
    <div className="glass-card p-4 md:p-5 flex flex-col">
      <SectionHeader
        title="Shop Transactions"
        icon={Store}
        iconClassName="text-green-400"
        count={logs.length}
        actions={
          <>
            <RedisKeyInfo redisKey={redisKey} />
            <button type="button" onClick={() => onExpand(logs, 'Shop Transaction Log', redisKey)} className="btn-icon btn-icon-sm" title="Expand Log View" aria-label="Expand Log View">
              <Maximize className="w-4 h-4" />
            </button>
          </>
        }
      />
      <div className="flex-grow">
        {logs.length === 0 ? <EmptyState bare compact icon={Store} title="No shop transactions" /> : 
          <VirtualizedLogList
            items={logs}
            renderItem={(entry) => (
              <div key={entry.id} className="border-b border-glass-200/10 py-2">
                <div className="flex justify-between items-center gap-2 text-xs text-glass-400 mb-1 tabular-nums">
                  <span>{new Date(parseInt(entry.id.split('-')[0])).toLocaleString()}</span>
                  <span className="badge badge-success font-mono">{entry.msg.action}</span>
                </div>
                <TransactionLogEntry entry={entry.msg} clusterId={clusterId} />
              </div>
            )}
          />
        }
      </div>
    </div>
  );
});

const TransferLog = memo(({ economyLog, loading, searchQuery, clusterId, onExpand, redisKey }: { economyLog: EconomyLogData | null, loading: boolean, searchQuery: string, clusterId: string, onExpand: (logs: any[], title: string, key: string | null) => void, redisKey: string | null }) => {
  const pairedTransfers = useMemo(() => {
    if (!economyLog) return [];

    const transferLogs = economyLog.XRANGE
      .filter(entry => ['SENT', 'RECEIVED'].includes(entry.msg.action))
      .map(entry => ({ ...entry, timestamp: parseInt(entry.id.split('-')[0]) }))
      .sort((a, b) => a.timestamp - b.timestamp);

    const transfers: any[] = [];
    const processedIds = new Set<string>();

    const sentLogs = transferLogs.filter(e => e.msg.action === 'SENT');
    const receivedLogs = transferLogs.filter(e => e.msg.action === 'RECEIVED');

    sentLogs.forEach(sentEntry => {
      if (processedIds.has(sentEntry.id)) return;

      const matchingReceived = receivedLogs.find(receivedEntry => 
        !processedIds.has(receivedEntry.id) &&
        sentEntry.msg.from === receivedEntry.msg.from &&
        sentEntry.msg.to === receivedEntry.msg.to &&
        sentEntry.msg.money === receivedEntry.msg.money &&
        Math.abs(sentEntry.timestamp - receivedEntry.timestamp) < 5000 // 5-second window
      );

      if (matchingReceived) {
        transfers.push({
          id: sentEntry.id,
          type: 'PAIRED',
          msg: sentEntry.msg,
          timestamp: sentEntry.timestamp,
        });
        processedIds.add(sentEntry.id);
        processedIds.add(matchingReceived.id);
      } else {
        transfers.push({ id: sentEntry.id, type: 'UNPAIRED', msg: sentEntry.msg, timestamp: sentEntry.timestamp });
      }
    });

    receivedLogs.forEach(receivedEntry => {
      if (!processedIds.has(receivedEntry.id)) {
        transfers.push({ id: receivedEntry.id, type: 'UNPAIRED', msg: receivedEntry.msg, timestamp: receivedEntry.timestamp });
      }
    });

    if (!searchQuery) {
      return transfers.sort((a, b) => b.timestamp - a.timestamp);
    }

    const lowerCaseQuery = searchQuery.toLowerCase();
    return transfers.filter(entry => {
        const { from, to, money, action, user } = entry.msg;
        const fromUsername = from ? usernameCacheService.getName(from)?.toLowerCase() : '';
        const toUsername = to ? usernameCacheService.getName(to)?.toLowerCase() : '';
        const userUsername = user ? usernameCacheService.getName(user)?.toLowerCase() : '';

        return (
            (from && from.toLowerCase().includes(lowerCaseQuery)) ||
            (fromUsername && fromUsername.includes(lowerCaseQuery)) ||
            (to && to.toLowerCase().includes(lowerCaseQuery)) ||
            (toUsername && toUsername.includes(lowerCaseQuery)) ||
            (user && user.toLowerCase().includes(lowerCaseQuery)) ||
            (userUsername && userUsername.includes(lowerCaseQuery)) ||
            money.includes(lowerCaseQuery) ||
            action.toLowerCase().includes(lowerCaseQuery)
        );
    }).sort((a, b) => b.timestamp - a.timestamp);
  }, [economyLog, searchQuery]);

  if (loading) return <LogPanelSkeleton />;

  return (
    <div className="glass-card p-4 md:p-5 flex flex-col">
      <SectionHeader
        title="Player Transfers"
        icon={Users}
        iconClassName="text-orange-400"
        count={pairedTransfers.length}
        actions={
          <>
            <RedisKeyInfo redisKey={redisKey} />
            <button type="button" onClick={() => onExpand(pairedTransfers, 'Player Transfer Log', redisKey)} className="btn-icon btn-icon-sm" title="Expand Log View" aria-label="Expand Log View">
              <Maximize className="w-4 h-4" />
            </button>
          </>
        }
      />
      <div className="flex-grow">
        {pairedTransfers.length === 0 ? <EmptyState bare compact icon={Users} title="No player transfers" /> : 
          <VirtualizedLogList
            items={pairedTransfers}
            renderItem={(entry) => (
              <div key={entry.id} className="border-b border-glass-200/10 py-2">
                <div className="flex justify-between items-center gap-2 text-xs text-glass-400 mb-1 tabular-nums">
                  <span>{new Date(entry.timestamp).toLocaleString()}</span>
                  {entry.type === 'UNPAIRED' && (
                    <span className="badge badge-danger font-mono">{entry.msg.action} (Unpaired)</span>
                  )}
                </div>
                {entry.type === 'PAIRED' ? (
                   <div className="flex items-start gap-3">
                     <ArrowLeftRight className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
                     <div className="text-sm min-w-0">
                       <PlayerProfile uuid={entry.msg.from!} /> sent ₡{entry.msg.money} to <PlayerProfile uuid={entry.msg.to!} />
                     </div>
                   </div>
                ) : (
                  <TransactionLogEntry entry={entry.msg} clusterId={clusterId} />
                )}
              </div>
            )}
          />
        }
      </div>
    </div>
  );
});

const AdminLog = memo(({ economyLog, loading, searchQuery, clusterId, onExpand, redisKey }: { economyLog: EconomyLogData | null, loading: boolean, searchQuery: string, clusterId: string, onExpand: (logs: any[], title: string, key: string | null) => void, redisKey: string | null }) => {
  const logs = useMemo(() => {
    let baseLogs = economyLog?.XRANGE.filter(entry => 
      ['MOD', 'SET'].includes(entry.msg.action)
    ) || [];

    if (searchQuery) {
        const lowerCaseQuery = searchQuery.toLowerCase();
        return baseLogs.filter(entry => {
            const { user, money, action } = entry.msg;
            const username = usernameCacheService.getName(user)?.toLowerCase();
            return (
                user.toLowerCase().includes(lowerCaseQuery) ||
                (username && username.includes(lowerCaseQuery)) ||
                money.includes(lowerCaseQuery) ||
                action.toLowerCase().includes(lowerCaseQuery)
            );
        });
    }

    return baseLogs.slice().reverse();
  }, [economyLog, searchQuery]);

  if (loading) return <LogPanelSkeleton />;

  return (
    <div className="glass-card p-4 md:p-5 flex flex-col">
      <SectionHeader
        title="Admin Actions"
        icon={Settings}
        iconClassName="text-yellow-400"
        count={logs.length}
        actions={
          <>
            <RedisKeyInfo redisKey={redisKey} />
            <button type="button" onClick={() => onExpand(logs, 'Admin Action Log', redisKey)} className="btn-icon btn-icon-sm" title="Expand Log View" aria-label="Expand Log View">
              <Maximize className="w-4 h-4" />
            </button>
          </>
        }
      />
      <div className="flex-grow">
        {logs.length === 0 ? <EmptyState bare compact icon={Settings} title="No admin actions" /> : 
          <VirtualizedLogList
            items={logs}
            renderItem={(entry) => (
              <div key={entry.id} className="border-b border-glass-200/10 py-2">
                <div className="flex justify-between items-center gap-2 text-xs text-glass-400 mb-1 tabular-nums">
                  <span>{new Date(parseInt(entry.id.split('-')[0])).toLocaleString()}</span>
                  <span className="badge badge-warning font-mono">{entry.msg.action}</span>
                </div>
                <TransactionLogEntry entry={entry.msg} clusterId={clusterId} />
              </div>
            )}
          />
        }
      </div>
    </div>
  );
});

export function EconomyView() {
  useDocumentTitle('Economy');
  const { clusterId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [configs, setConfigs] = useState<ServerConfig[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<string>('');
  const [shopLog, setShopLog] = useState<EconomyLogData | null>(null);
  const [shopLogKey, setShopLogKey] = useState<string | null>(null);
  const [auctionHouseData, setAuctionHouseData] = useState<ShopData | null>(null);
  const [auctionHouseDataKey, setAuctionHouseDataKey] = useState<string | null>(null);
  const [adminShopData, setAdminShopData] = useState<ShopData | null>(null);
  const [adminShopDataKey, setAdminShopDataKey] = useState<string | null>(null);
  const [chestShopData, setChestShopData] = useState<ShopData | null>(null);
  const [chestShopDataKey, setChestShopDataKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { status: freshness, load } = useSwrLoader();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeShop, setActiveShop] = useState<ShopCategory>('ah');
  const [expandedLog, setExpandedLog] = useState<ExpandedLogState | null>(null);
  const [activeLogTab, setActiveLogTab] = useState<'ah'|'shop'|'transfers'|'admin'>('ah');

  useEffect(() => {
    if (shopLog) {
      const uuids = new Set<string>();
      shopLog.XRANGE.forEach(entry => {
        if (entry.msg.user) uuids.add(entry.msg.user);
        if (entry.msg.from) uuids.add(entry.msg.from);
        if (entry.msg.to) uuids.add(entry.msg.to);
      });
      usernameCacheService.prefetchUsernames(Array.from(uuids));
    }
  }, [shopLog]);

  useEffect(() => {
    const allListings = [
      ...(auctionHouseData?.listings || []),
      ...(adminShopData?.listings || []),
      ...(chestShopData?.listings || []),
    ];

    if (allListings.length > 0) {
      const uuids = new Set<string>();
      allListings.forEach(item => {
        if (item.seller) uuids.add(item.seller);
      });
      usernameCacheService.prefetchUsernames(Array.from(uuids));
    }
  }, [auctionHouseData, adminShopData, chestShopData]);

  const transferCount = useMemo(() => {
    if (!shopLog) return 0;
    
    const transferLogs = shopLog.XRANGE
      .filter(entry => ['SENT', 'RECEIVED'].includes(entry.msg.action))
      .map(entry => ({ ...entry, timestamp: parseInt(entry.id.split('-')[0]) }));

    let count = 0;
    const processedIds = new Set<string>();
    const sentLogs = transferLogs.filter(e => e.msg.action === 'SENT');
    const receivedLogs = transferLogs.filter(e => e.msg.action === 'RECEIVED');

    sentLogs.forEach(sentEntry => {
      if (processedIds.has(sentEntry.id)) return;

      const matchingReceived = receivedLogs.find(receivedEntry => 
        !processedIds.has(receivedEntry.id) &&
        sentEntry.msg.from === receivedEntry.msg.from &&
        sentEntry.msg.to === receivedEntry.msg.to &&
        sentEntry.msg.money === receivedEntry.msg.money &&
        Math.abs(sentEntry.timestamp - receivedEntry.timestamp) < 5000
      );

      if (matchingReceived) {
        count++;
        processedIds.add(sentEntry.id);
        processedIds.add(matchingReceived.id);
      } else {
        count++; // Count unpaired sent as one transfer
      }
    });

    receivedLogs.forEach(receivedEntry => {
      if (!processedIds.has(receivedEntry.id)) {
        count++; // Count unpaired received as one transfer
      }
    });

    return count;
  }, [shopLog]);

  useEffect(() => {
    async function fetchClusters() {
      try {
        const configList = await configService.getConfigs();
        const uniqueClusters = configList.reduce((acc, current) => {
          if (current.cluster_name && !acc.find(item => item.cluster_name === current.cluster_name)) {
            acc.push(current);
          }
          return acc;
        }, [] as ServerConfig[]);
        setConfigs(uniqueClusters);
        
        // --- Prefetch images ---
        const imageUrls = uniqueClusters.map(c => c.thumbnail_url);
        prefetchService.prefetchImages(imageUrls);
        // -----------------------

        if (clusterId && uniqueClusters.some(c => c.cluster_name === clusterId)) {
          setSelectedCluster(clusterId);
        } else if (uniqueClusters.length > 0) {
          const defaultCluster = uniqueClusters[0].cluster_name;
          setSelectedCluster(defaultCluster);
          navigate(`/economy/${defaultCluster}`, { replace: true });
        } else { 
          setLoading(false); 
        }
      } catch (err) {
        setError('Failed to fetch cluster list.');
        setLoading(false);
      }
    }
    fetchClusters();
  }, []);

  // Cache, then recache on load: the last copies of this cluster's four documents paint at once
  // (if all four exist), Redis is re-read every time, and the fresh answers replace them.
  const fetchDataForCluster = useCallback(async (force = false) => {
    if (!selectedCluster) return;
    setLoading(true);
    setError(null);
    let superseded = false;
    try {
      const outcome = await load(
        [
          read(`redis:cluster:${selectedCluster}:economy-log`, () => gameRedisService.getEconomyLog(selectedCluster), (r) => {
            setShopLog(r.data);
            setShopLogKey(r.key);
          }),
          read(`redis:cluster:${selectedCluster}:shop:AH`, () => gameRedisService.getAuctionHouseData(selectedCluster), (r) => {
            setAuctionHouseData(r.data);
            setAuctionHouseDataKey(r.key);
          }),
          read(`redis:cluster:${selectedCluster}:shop:AS`, () => gameRedisService.getAdminShopData(selectedCluster), (r) => {
            setAdminShopData(r.data);
            setAdminShopDataKey(r.key);
          }),
          read(`redis:cluster:${selectedCluster}:shop:CS`, () => gameRedisService.getChestShopData(selectedCluster), (r) => {
            setChestShopData(r.data);
            setChestShopDataKey(r.key);
          }),
        ],
        { force, onCached: () => setLoading(false) }
      );
      superseded = outcome.superseded;
      if (superseded) return;

      // With cached data on screen ('stale') the freshness badge reports the failed re-read;
      // otherwise the per-document errors show as before.
      if (outcome.status.phase !== 'stale') {
        const labels = ['economy log', 'auction house data', 'admin shop data', 'chest shop data'];
        outcome.results.forEach((result, i) => {
          if (result.status === 'rejected') {
            console.error(`Failed to fetch ${labels[i]}:`, result.reason);
            setError(prev => prev ? `${prev} Failed to fetch ${labels[i]}.` : `Failed to fetch ${labels[i]}.`);
          }
        });
      }
    } catch (err) {
      setError(`An unexpected error occurred while fetching cluster data.`);
      console.error(err);
    } finally {
      if (!superseded) setLoading(false);
    }
  }, [selectedCluster, load]);

  useEffect(() => {
    if (selectedCluster && selectedCluster !== clusterId) {
      navigate(`/economy/${selectedCluster}`);
    }
    fetchDataForCluster();
  }, [selectedCluster, fetchDataForCluster]);

  const handleExpandLog = (logs: any[], title: string, key: string | null) => {
    setExpandedLog({ logs, title, key });
  };

  return (
    <div className="space-y-6">
      <ClusterSelector
        eyebrow="Economy & Game"
        title="Economy Management"
        description="View economy logs and auction house data by cluster."
        configs={configs}
        selectedCluster={selectedCluster}
        onSelect={setSelectedCluster}
      />
      
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Fuzzy search page content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="glass-input w-full pl-9"
          />
        </div>
        <div className="flex items-center gap-3 md:justify-end">
          {shopLog && !loading && (
            <span className="text-xs text-glass-400 tabular-nums md:text-right">
              {shopLog.XRANGE.length.toLocaleString()} log entries
            </span>
          )}
          <DataFreshness status={freshness} onRefresh={() => fetchDataForCluster(true)} />
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span>{error}</span>
        </div>
      )}
      
      {/* Economy Summary */}
      {shopLog && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            label="Auction House"
            value={shopLog.XRANGE.filter(entry => ['BUY_AH', 'SELL_AH', 'LISTED_AH'].includes(entry.msg.action)).length.toLocaleString()}
            icon={Gavel}
            tone="info"
          />
          <StatTile
            label="Shop Transactions"
            value={shopLog.XRANGE.filter(entry => ['BUY_AS', 'SELL_AS', 'BUY_CS', 'SELL_CS'].includes(entry.msg.action)).length.toLocaleString()}
            icon={Store}
            tone="success"
          />
          <StatTile
            label="Player Transfers"
            value={transferCount.toLocaleString()}
            icon={Users}
            tone="orange"
          />
          <StatTile
            label="Admin Actions"
            value={shopLog.XRANGE.filter(entry => ['MOD', 'SET'].includes(entry.msg.action)).length.toLocaleString()}
            icon={Settings}
            tone="warning"
          />
        </div>
      )}
      
      {/* Categorized Transaction Logs */}
      {isMobile ? (
        <div className="space-y-4">
          <div className="segmented w-full" role="tablist" aria-label="Economy log sections">
            {([
              { key: 'ah' as const, label: 'Auction House', icon: <Gavel className="w-4 h-4" /> },
              { key: 'shop' as const, label: 'Shop', icon: <Store className="w-4 h-4" /> },
              { key: 'transfers' as const, label: 'Transfers', icon: <Users className="w-4 h-4" /> },
              { key: 'admin' as const, label: 'Admin', icon: <Settings className="w-4 h-4" /> },
            ]).map(tab => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeLogTab === tab.key}
                onClick={() => setActiveLogTab(tab.key)}
                data-active={activeLogTab === tab.key ? 'true' : 'false'}
                className="segmented-item flex-1"
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
          {activeLogTab === 'ah' && <AuctionHouseLog economyLog={shopLog} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} onExpand={handleExpandLog} redisKey={shopLogKey} />}
          {activeLogTab === 'shop' && <ShopLog economyLog={shopLog} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} onExpand={handleExpandLog} redisKey={shopLogKey} />}
          {activeLogTab === 'transfers' && <TransferLog economyLog={shopLog} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} onExpand={handleExpandLog} redisKey={shopLogKey} />}
          {activeLogTab === 'admin' && <AdminLog economyLog={shopLog} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} onExpand={handleExpandLog} redisKey={shopLogKey} />}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
          <AuctionHouseLog economyLog={shopLog} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} onExpand={handleExpandLog} redisKey={shopLogKey} />
          <ShopLog economyLog={shopLog} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} onExpand={handleExpandLog} redisKey={shopLogKey} />
          <TransferLog economyLog={shopLog} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} onExpand={handleExpandLog} redisKey={shopLogKey} />
          <AdminLog economyLog={shopLog} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} onExpand={handleExpandLog} redisKey={shopLogKey} />
        </div>
      )}
      
      {/* Full-width Shop Listings at bottom */}
      <div className="glass-card p-4 md:p-5">
        <div className="tabs mb-4" role="tablist" aria-label="Shop listings">
          <button type="button" role="tab" aria-selected={activeShop === 'ah'} onClick={() => setActiveShop('ah')} data-active={activeShop === 'ah' ? 'true' : 'false'} className="tab">Auction House</button>
          <button type="button" role="tab" aria-selected={activeShop === 'as'} onClick={() => setActiveShop('as')} data-active={activeShop === 'as' ? 'true' : 'false'} className="tab">Admin Shop</button>
          <button type="button" role="tab" aria-selected={activeShop === 'cs'} onClick={() => setActiveShop('cs')} data-active={activeShop === 'cs' ? 'true' : 'false'} className="tab">Chest Shops</button>
          <div className="ml-auto pl-2 pb-2 shrink-0">
            <RedisKeyInfo 
              redisKey={
                activeShop === 'ah' ? auctionHouseDataKey :
                activeShop === 'as' ? adminShopDataKey :
                chestShopDataKey
              } 
            />
          </div>
        </div>

        {activeShop === 'ah' && <ListingsComponent listings={auctionHouseData?.listings} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} category="Auction House" />}
        {activeShop === 'as' && <AdminShopComponent shopData={adminShopData} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} />}
        {activeShop === 'cs' && <ListingsComponent listings={chestShopData?.listings} loading={loading} searchQuery={searchQuery} clusterId={selectedCluster} category="Chest Shop" />}
      </div>

      {expandedLog && (
        <ExpandedLogModal 
            title={expandedLog.title}
            logs={expandedLog.logs}
            clusterId={selectedCluster}
            onClose={() => setExpandedLog(null)}
            redisKey={expandedLog.key}
            searchKeys={['msg.user', 'msg.item', 'msg.itemDisplayName', 'msg.money', 'msg.action', 'msg.from', 'msg.to']}
        />
      )}
    </div>
  );
}
